import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, upsertUserProfile } from '../services/profilesService';
import { getVendor, getVendorDirectoryEntry, upsertVendor, findVendorByName, listVendorsLight } from '../services/vendorsService';
import { listEvents } from '../services/eventsService';
import app, { auth } from '../../sdk_initialization';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeApp as initializeSecondaryApp, getApp as getFirebaseApp } from 'firebase/app';
import { EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { userTypeOptions, userStatusOptions, vendorStatusOptions, countryCodeOptions, stateCodeOptions } from '../config/lookups';
import { getBusinessTypes } from '../services/lookupsService';
import { deleteBoothApplication } from '../services/applicationsService';
import { listTransactionsForProfile } from '../services/transactionsService';
import { sendEmail } from '../services/emailService';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { uid: uidParam } = useParams();
  const targetUid = uidParam || currentUser?.uid;
  const editingOtherUser = !!uidParam && uidParam !== currentUser?.uid;
  const isAnonymous = !!currentUser?.isAnonymous;
  const fromAdmin = (location?.state && location.state.fromAdmin) || (new URLSearchParams(location?.search || '').get('src') === 'admin');
  const goAfterDone = () => {
    if (fromAdmin) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [bizPhoneError, setBizPhoneError] = useState('');
  // `country` controls the address country selector. Leave empty by default
  // so the form does not pre-select 'United States' when no profile exists.
  const [country, setCountry] = useState('');
  // `userAreaCode` is the phone prefix for the user's personal phone
  // saved on the `profiles` document. `businessAreaCode` is the phone
  // prefix for the business phone and is saved on the `vendors` document.
  const [userAreaCode, setUserAreaCode] = useState('+1');
  const [businessAreaCode, setBusinessAreaCode] = useState('+1');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    yob: '',
    phone: '',
    phoneNumber: '',
    email: '',
    userType: 'Vendor',
    userStatus: 'Active',
    vendorId: '',
    vendorNumber: '',
    vendorStatus: 'Active',
    vendorAdmin: '',
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    businessDescription: '',
    federalId: '',
    businessUseUserEmail: false,
    businessPhoneNumber: false,
    businessAddressStreet: '',
    businessAddressCity: '',
    businessAddressState: '',
    businessAddressZip: '',
    businessType: '',
    businessCountryName: '', // free-form country name when country === 'OTHER'
    vendorRole: '',
    createdAt: null,
    updatedAt: null,
    acknowledgedPolicies: false,
  });

  const countries = useMemo(() => countryCodeOptions, []);

  const [businessTypes, setBusinessTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorLoadError, setVendorLoadError] = useState(null);
  const [linkedVendor, setLinkedVendor] = useState(null);
  const [linkedVendorLoading, setLinkedVendorLoading] = useState(false);
  const [linkedVendorError, setLinkedVendorError] = useState(null);
  const [vendorLinkedFromDirectory, setVendorLinkedFromDirectory] = useState(false);
  const [vendorQuery, setVendorQuery] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  // Two-step flow: save profile first, then choose create/link vendor.
  const [vendorSetupMode, setVendorSetupMode] = useState('');
  const [hasPersistedProfile, setHasPersistedProfile] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showDuplicateNamePopup, setShowDuplicateNamePopup] = useState(false);
  const [duplicateNamePopupMessage, setDuplicateNamePopupMessage] = useState('');
  const [pendingVendorToLink, setPendingVendorToLink] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [organizerEvents, setOrganizerEvents] = useState([]);
  const [eventAdminEvents, setEventAdminEvents] = useState([]);
  const [roleEventsLoading, setRoleEventsLoading] = useState(false);
  const [roleEventsError, setRoleEventsError] = useState(null);
  const [vendorAdminDisplayName, setVendorAdminDisplayName] = useState('');
  const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState('');
  const successTimer = useRef(null);
  const formRef = useRef(null);
  const protectedVendorLoaded = useRef(false);
  const originalVendorId = useRef('');
  // vendorRole is binary: 'admin' = this user owns/created the vendor and can edit it,
  // 'user' = this user is linked to another vendor and must not edit business records.
  // A user profile is tied to at most one vendor at a time.
  const isVendorAdmin = profile?.vendorRole === 'admin';
  // Strict rule: when vendorRole is 'user', business section is always read-only.
  const isLinkedReadOnlyBusiness = Boolean(profile?.vendorId) && profile?.vendorRole === 'user';
  const [myProfile, setMyProfile] = useState(null);
  const [myProfileLoading, setMyProfileLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!currentUser || !currentUser.uid) return;
      try {
        setMyProfileLoading(true);
        const p = await getUserProfile(currentUser.uid);
        if (!mounted) return;
        setMyProfile(p || null);
      } catch (e) {
        if (!mounted) return;
        setMyProfile(null);
      } finally {
        if (!mounted) return;
        setMyProfileLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [currentUser]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!targetUid) {
        setOrganizerEvents([]);
        setEventAdminEvents([]);
        return;
      }

      try {
        setRoleEventsLoading(true);
        setRoleEventsError(null);

        const allEvents = await listEvents();
        const uidLower = String(targetUid || '').trim().toLowerCase();

        const organizerMatches = (allEvents || []).filter(ev => {
          const organizerList =
            (Array.isArray(ev?.organizers) && ev.organizers) ||
            (Array.isArray(ev?.eventOrganizers) && ev.eventOrganizers) ||
            (Array.isArray(ev?.EventOrganizers) && ev.EventOrganizers) ||
            (Array.isArray(ev?.eventOrganizerList) && ev.eventOrganizerList) ||
            (Array.isArray(ev?.EventOrganizerList) && ev.EventOrganizerList) ||
            [];
          return organizerList.some(a => String(a || '').trim().toLowerCase() === uidLower);
        });

        const eventAdminMatches = (allEvents || []).filter(ev =>
          Array.isArray(ev?.eventAdmins) &&
          ev.eventAdmins.some(a => String(a || '').trim().toLowerCase() === uidLower)
        );

        organizerMatches.sort((a, b) => String(a?.name || a?.id || '').localeCompare(String(b?.name || b?.id || '')));
        eventAdminMatches.sort((a, b) => String(a?.name || a?.id || '').localeCompare(String(b?.name || b?.id || '')));

        if (!mounted) return;
        setOrganizerEvents(organizerMatches);
        setEventAdminEvents(eventAdminMatches);
      } catch (e) {
        if (!mounted) return;
        setOrganizerEvents([]);
        setEventAdminEvents([]);
        setRoleEventsError('Unable to load event role assignments.');
      } finally {
        if (!mounted) return;
        setRoleEventsLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [targetUid]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const vendorAdminUid = String(profile?.vendorAdmin || '').trim();
      if (!vendorAdminUid) {
        setVendorAdminDisplayName('');
        return;
      }

      try {
        // Reuse current user's already loaded profile when possible.
        if (currentUser?.uid && String(currentUser.uid) === vendorAdminUid) {
          const ownName =
            `${myProfile?.firstName || ''} ${myProfile?.lastName || ''}`.trim() ||
            myProfile?.name ||
            currentUser?.displayName ||
            myProfile?.email ||
            currentUser?.email ||
            vendorAdminUid;
          if (!mounted) return;
          setVendorAdminDisplayName(ownName);
          return;
        }

        const adminProfile = await getUserProfile(vendorAdminUid);
        const resolvedName =
          `${adminProfile?.firstName || ''} ${adminProfile?.lastName || ''}`.trim() ||
          adminProfile?.name ||
          adminProfile?.email ||
          vendorAdminUid;
        if (!mounted) return;
        setVendorAdminDisplayName(resolvedName);
      } catch (e) {
        if (!mounted) return;
        // Fallback to UID when profile read is not permitted.
        setVendorAdminDisplayName(vendorAdminUid);
      }
    })();

    return () => { mounted = false; };
  }, [profile?.vendorAdmin, currentUser?.uid, currentUser?.displayName, currentUser?.email, myProfile]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const explicitName = String(profile?.updatedByName || '').trim();
      if (explicitName) {
        setLastUpdatedByDisplayName(explicitName);
        return;
      }

      const updatedByUid = String(profile?.updatedBy || '').trim();
      if (!updatedByUid) {
        setLastUpdatedByDisplayName('');
        return;
      }

      try {
        if (currentUser?.uid && String(currentUser.uid) === updatedByUid) {
          const ownName =
            `${myProfile?.firstName || ''} ${myProfile?.lastName || ''}`.trim() ||
            myProfile?.name ||
            currentUser?.displayName ||
            myProfile?.email ||
            currentUser?.email ||
            updatedByUid;
          if (!mounted) return;
          setLastUpdatedByDisplayName(ownName);
          return;
        }

        const updaterProfile = await getUserProfile(updatedByUid);
        const resolvedName =
          `${updaterProfile?.firstName || ''} ${updaterProfile?.lastName || ''}`.trim() ||
          updaterProfile?.name ||
          updaterProfile?.email ||
          updatedByUid;
        if (!mounted) return;
        setLastUpdatedByDisplayName(resolvedName);
      } catch (e) {
        if (!mounted) return;
        setLastUpdatedByDisplayName(updatedByUid);
      }
    })();

    return () => { mounted = false; };
  }, [profile?.updatedByName, profile?.updatedBy, currentUser?.uid, currentUser?.displayName, currentUser?.email, myProfile]);

  const isCurrentUserAdmin = (myProfile && myProfile.userType === 'Admin');
  // Use the default app's Functions client so the current user's ID token
  // is attached automatically (same approach as `paymentsService.js`).
  // If you need cross-project callables in the future, implement a server
  // proxy instead of relying on a secondary client-side app.
  const functions = (() => {
    try {
      const region = import.meta.env.VITE_FUNCTIONS_REGION || undefined;
      return getFunctions(app, region);
    } catch (e) {
      try { return getFunctions(app); } catch (err) { return null; }
    }
  })();

  useEffect(() => {
    const run = async () => {
  if (!targetUid) { setLoading(false); return; }
      try {
          setLoading(true);
          // Build defaults from auth user (with provider fallbacks) FIRST
          const authDefaults = (() => {
            const providerWithEmail = Array.isArray(currentUser.providerData)
              ? currentUser.providerData.find(p => !!p?.email) || currentUser.providerData[0]
              : null;
            const email = currentUser.email || providerWithEmail?.email || '';
            const phone = currentUser.phoneNumber || providerWithEmail?.phoneNumber || '';
            let firstName = '';
            let lastName = '';
            if (currentUser.displayName) {
              const parts = currentUser.displayName.trim().split(/\s+/);
              firstName = parts[0] || '';
              lastName = parts.slice(1).join(' ') || '';
            }
            return { email, phone, firstName, lastName };
          })();

          // Try to load existing profile; if it fails (e.g., rules), fall back to auth defaults
          let existing = null;
          try {
            existing = await getUserProfile(targetUid);
          } catch (e) {
            console.warn('Profile read failed, using auth defaults only:', e?.message || e);
          }

          if (existing) {
            setHasPersistedProfile(true);
            // Merge: keep existing, but if empty fields, fallback to auth defaults
            setProfile(prev => ({
                ...authDefaults,
                ...existing,
                firstName: existing.firstName || authDefaults.firstName,
                lastName: existing.lastName || authDefaults.lastName,
                phone: existing.phone || authDefaults.phone,
                email: existing.email || authDefaults.email,
              }));
            // Ensure countryCode and phone UI are populated correctly. Prefer an
            // explicit `countryCode` stored on the profile. If not present, try
            // to parse a leading +country from the stored phone (older format),
            // otherwise fall back to auth defaults or `+1`.
                try {
                const existingPhone = existing.phone || authDefaults.phone || '';
                // Support both legacy `countryCode` and new `userAreaCode` on profiles
                const founduserAreaCode = existing.userAreaCode ?? existing.countryCode;
                if (founduserAreaCode) {
                  setUserAreaCode(founduserAreaCode);
                  // keep phone digits portion (strip non-digits)
                  const only = (existingPhone || '').replace(/\D/g, '');
                  if (only) setProfile(prev => ({ ...prev, phone: only.slice(-10) }));
                } else {
                  // Use libphonenumber to parse and extract a reliable country calling code
                  try {
                    const parsed = parsePhoneNumberFromString(existingPhone || authDefaults.phone || '');
                    if (parsed && parsed.countryCallingCode) {
                      setUserAreaCode(`+${parsed.countryCallingCode}`);
                      const national = parsed.nationalNumber || '';
                      if (national) setProfile(prev => ({ ...prev, phone: national.slice(-10) }));
                    }
                  } catch (ppErr) {
                    // fallback to previous heuristic: try simple regex extraction
                    if (/^\+\d{1,3}/.test(existingPhone)) {
                      const m = existingPhone.match(/^(\+\d{1,3})(.*)$/);
                      if (m) {
                        setUserAreaCode(m[1]);
                        const rest = (m[2] || '').replace(/\D/g, '');
                        if (rest) setProfile(prev => ({ ...prev, phone: rest.slice(-10) }));
                      }
                    } else if (authDefaults.phone && /^\+\d{1,3}/.test(authDefaults.phone)) {
                      const ma = authDefaults.phone.match(/^(\+\d{1,3})(.*)$/);
                      if (ma) {
                        setUserAreaCode(ma[1]);
                        const rest = (ma[2] || '').replace(/\D/g, '');
                        if (rest) setProfile(prev => ({ ...prev, phone: rest.slice(-10) }));
                      }
                    }
                  }
                }
                // Also normalize business phone into countryCode + 10-digit portion
                try {
                  const existingBiz = existing.businessPhone || '';
                  if (existingBiz) {
                    try {
                      const parsedBiz = parsePhoneNumberFromString(existingBiz || '');
                      if (parsedBiz && parsedBiz.countryCallingCode) {
                        setBusinessAreaCode(`+${parsedBiz.countryCallingCode}`);
                        const nationalBiz = parsedBiz.nationalNumber || '';
                        if (nationalBiz) setProfile(prev => ({ ...prev, businessPhone: nationalBiz.slice(-10) }));
                      } else {
                        const onlyBiz = existingBiz.replace(/\D/g, '');
                        if (onlyBiz) setProfile(prev => ({ ...prev, businessPhone: onlyBiz.slice(-10) }));
                      }
                    } catch (pErr) {
                      const onlyBiz = existingBiz.replace(/\D/g, '');
                      if (onlyBiz) setProfile(prev => ({ ...prev, businessPhone: onlyBiz.slice(-10) }));
                    }
                  }
                } catch (bizParseErr) {
                  // non-fatal
                }
            } catch (parseErr) {
            // non-fatal: keep existing countryCode state
            }
            // Prefill vendor metadata. Only populate full vendor details into the
            // profile form when the current user is the vendor admin or when an
            // admin is editing. For ordinary users associated with an existing
            // vendor we only keep the vendorId/vendorNumber/vendorStatus to avoid
            // copying business fields into the profiles collection.
            if (existing.vendorId) {
              // Prefer the lightweight directory entry when available to avoid
              // protected reads against `vendors` that can trigger permission errors.
              const dirEntry = (vendors || []).find(x => x.id === existing.vendorId);
              if (dirEntry) {
                // Use directory info for regular users. If the profile has an
                // explicit vendorRole='admin', prefer the protected vendor doc
                // so admins/editors can see/edit full vendor fields. Also allow
                // protected reads for explicit vendorAdmin matches, self-view,
                // admin editors, or when editing another user.
                const roleIsAdmin = existing.vendorRole === 'admin';
                const isVendorAdminExplicit = currentUser && dirEntry.vendorAdmin && (String(dirEntry.vendorAdmin) === String(currentUser.uid));
                // Only load the full (protected) vendor doc for vendor admins and staff editors.
                // Linked members (vendorRole='user') do not need editable vendor data on the profile page.
                const shouldTryProtected = roleIsAdmin || isVendorAdminExplicit || fromAdmin || editingOtherUser;
                if (shouldTryProtected) {
                  // Try to fetch the protected vendor doc; fall back to directory
                  // entry if the protected read is not allowed.
                  try {
                    const vendor = await getVendor(existing.vendorId);
                    if (vendor) {
                      // Normalize vendor phone into shared `countryCode` + 10-digit businessPhone digits.
                      // Prefer the new canonical `phoneNumber` + `countryCode` fields on the vendor doc.
                      let bizPhoneDigits = '';
                      try {
                        if (vendor?.phoneNumber) {
                            bizPhoneDigits = String(vendor.phoneNumber || '').replace(/\D/g, '').slice(-10);
                            // Prefer an explicit businessAreaCode field on the vendor doc;
                            // fall back to legacy `countryCode` if present.
                            const bc = vendor.businessAreaCode ?? vendor.countryCode;
                            if (bc) setBusinessAreaCode(bc);
                          } else if (vendor?.phone) {
                          // Fallback for legacy `phone` field (E.164 or raw)
                          const b = String(vendor.phone || '');
                          if (/^\+\d{1,3}/.test(b)) {
                            const mb = b.match(/^(\+\d{1,3})(.*)$/);
                            if (mb) {
                              // Legacy vendor `phone` stores E.164; the prefix is
                              // the business country calling code, so set the
                              // `businessAreaCode` (not the user's country code).
                              setBusinessAreaCode(mb[1]);
                              bizPhoneDigits = (mb[2] || '').replace(/\D/g, '').slice(-10);
                            }
                          } else {
                            bizPhoneDigits = b.replace(/\D/g, '').slice(-10);
                          }
                        }
                      } catch (e) {
                        // ignore
                      }
                      setProfile(prev => ({
                        ...prev,
                        vendorNumber: vendor.vendorNumber || '',
                        vendorStatus: vendor.vendorStatus || 'Active',
                        vendorAdmin: vendor.vendorAdmin || '',
                        businessName: vendor.name || '',
                        businessEmail: vendor.email || '',
                        businessPhone: bizPhoneDigits || '',
                        businessDescription: vendor.description || '',
                        federalId: vendor.federalId || '',
                        businessAddressStreet: vendor.addressStreet || '',
                        businessAddressCity: vendor.addressCity || '',
                        businessAddressState: vendor.addressState || '',
                        businessAddressZip: vendor.addressZip || '',
                        businessType: vendor.type || '',
                        businessCountryName: vendor.countryName || '',
                        vendorId: existing.vendorId
                      }));
                    } else {
                      // Protected vendor doc not available; fall back to directory entry
                      setProfile(prev => ({
                        ...prev,
                        vendorNumber: dirEntry.vendorNumber || '',
                        vendorStatus: dirEntry.vendorStatus || 'Active',
                        vendorAdmin: dirEntry.vendorAdmin || '',
                        vendorId: existing.vendorId,
                        businessName: dirEntry.name || ''
                      }));
                    }
                  } catch (e) {
                    // Suppress permission-denied noise — we'll still show a usable form
                    if (!(e?.code === 'permission-denied' || e?.message?.toLowerCase?.().includes('insufficient permissions'))) {
                      console.warn('Failed to load vendor info:', e?.message || e);
                    }
                  }
                }
              }
            }
            // remember original vendorId so we can detect new associations on save
            originalVendorId.current = existing.vendorId || '';
        } else {
          setHasPersistedProfile(false);
          // No profile yet: prefill from auth defaults
          originalVendorId.current = '';
          setProfile(prev => ({ ...prev, ...authDefaults }));
          // If auth defaults include a phone in +<country>... format,
          // split it into countryCode and the 10-digit phone portion.
          try {
            const ap = authDefaults.phone || '';
            try {
              const parsed = parsePhoneNumberFromString(ap || '');
              if (parsed && parsed.countryCallingCode) {
                setUserAreaCode(`+${parsed.countryCallingCode}`);
                const national = parsed.nationalNumber || '';
                if (national) setProfile(prev => ({ ...prev, phone: national.slice(-10) }));
              } else {
                const only = (ap || '').replace(/\D/g, '');
                if (only) setProfile(prev => ({ ...prev, phone: only.slice(-10) }));
              }
            } catch (pErr) {
              const only = (ap || '').replace(/\D/g, '');
              if (only) setProfile(prev => ({ ...prev, phone: only.slice(-10) }));
            }
          } catch (e) {
            // ignore parse errors
          }
        }
          } catch (e) {
        console.error('Failed to load profile', e);
        // Non-blocking: show a soft error but keep the form usable
        setError('Unable to load saved profile from the database. Using your sign-in info as defaults.');
      } finally {
        // Ensure a sensible default area code is always present
        try { setUserAreaCode(prev => prev || '+1'); } catch (_) {}
        setLoading(false);
      }
    };
    run();
  }, [currentUser, targetUid, vendors]);

  // Load business types from Firestore
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const types = await getBusinessTypes();
        if (mounted) setBusinessTypes(types);
      } catch (e) {
        console.warn('Failed to load business types:', e?.message || e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load lightweight vendor directory for search/dropdown (used for association)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setVendorLoading(true);
        setVendorLoadError(null);
        const list = await listVendorsLight();
        if (!mounted) return;
        setVendors(list || []);
        setFilteredVendors(list || []);
      } catch (e) {
        console.warn('Failed to load vendor directory:', e?.message || e);
        if (!mounted) return;
        setVendorLoadError(e?.message || String(e));
        setVendors([]);
        setFilteredVendors([]);
      } finally {
        if (!mounted) return;
        setVendorLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Debounce the vendorQuery to avoid filtering on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(vendorQuery.trim()), 250);
    return () => clearTimeout(t);
  }, [vendorQuery]);

  useEffect(() => {
    if (!debouncedQuery) {
      setFilteredVendors(vendors || []);
      setHighlightIndex(-1);
      // show available vendors when input is empty/focused
      if ((vendors || []).length > 0) setDropdownOpen(true);
      return;
    }
    const ql = debouncedQuery.toLowerCase();
    const found = (vendors || []).filter(v => {
      const name = (v.name || '').toLowerCase();
      const city = (v.city || '').toLowerCase();
      const state = (v.state || '').toLowerCase();
      const type = (v.type || '').toLowerCase();
      const vnum = (v.vendorNumber || '').toLowerCase();
      return name.includes(ql) || city.includes(ql) || state.includes(ql) || type.includes(ql) || vnum.includes(ql);
    });
    setFilteredVendors(found.slice(0, 12));
    setHighlightIndex(found.length > 0 ? 0 : -1);
    setDropdownOpen(true);
  }, [debouncedQuery, vendors]);

  const handleVendorQueryChange = (q) => {
    setVendorQuery(q);
    setDropdownOpen(true);
  };

  const handleSelectVendor = (vendorId) => {
    const v = (vendors || []).find(x => x.id === vendorId);
    if (!v) return;
    // Instead of immediately linking, prompt the user to confirm linking
    setPendingVendorToLink(v);
    setDropdownOpen(false);
  };

  const handleUnlinkVendor = async () => {
    // Only allow unlink when the link is NOT approved. If approved, the
    // vendor organizer must perform the unlink to maintain data/financial
    // integrity.
    try {
      if (profile.linkApproval === 'approved') {
        // Show a modal/popup to inform the user instead of an inline error
        try { window.alert('This account is approved and cannot be unlinked here. Please contact the vendor organizer to request unlinking.'); } catch (e) { setError('This account is approved and cannot be unlinked here. Please contact the vendor organizer to request unlinking.'); }
        return;
      }

      // Perform unlink: clear vendor association and mark linkApproval as not applicable
      setSaving(true);
      setError(null);
      try {
        const updaterName =
          `${myProfile?.firstName || ''} ${myProfile?.lastName || ''}`.trim() ||
          myProfile?.name ||
          currentUser?.displayName ||
          myProfile?.email ||
          currentUser?.email ||
          currentUser?.uid ||
          '';
        await upsertUserProfile(targetUid, {
          vendorId: '',
          vendorRole: '',
          linkApproval: 'n/a',
          updatedBy: currentUser?.uid || '',
          updatedByName: updaterName,
        });
      } catch (saveErr) {
        console.error('Failed to persist unlink on profile', saveErr);
        setError('Failed to unlink vendor from profile. Please try again.');
        setSaving(false);
        return;
      }

      // Refresh page to reflect changes
      try {
        window.location.reload();
      } catch (rErr) {
        // Fallback: navigate home
        try { navigate('/'); } catch (nErr) {}
      } finally {
        setSaving(false);
      }
    } catch (err) {
      console.error('Unexpected error during unlink', err);
      setError('An unexpected error occurred while unlinking.');
    }
  };

  const startCreateVendor = () => {
    // Prepare UI state immediately
    setVendorSetupMode('create');
    setDropdownOpen(false);
    setVendorQuery('');
    setFilteredVendors([]);
    setHighlightIndex(-1);
  };

  const startLinkVendor = () => {
    setVendorSetupMode('link');
    setVendorQuery('');
    setFilteredVendors(vendors || []);
    setHighlightIndex((vendors || []).length > 0 ? 0 : -1);
    setDropdownOpen(true);
  };

  // When a vendor is linked, fetch lightweight vendor info to display to the user
  useEffect(() => {
    let mounted = true;
    (async () => {
      const vid = profile.vendorId;
      if (!vid) {
        setLinkedVendor(null);
        setLinkedVendorError(null);
        setLinkedVendorLoading(false);
        return;
      }

      // If the vendor was selected from the directory we already have its
      // lightweight info locally; prefer that to avoid reading the protected
      // `vendors` document which may be blocked by security rules.
      // Always prefer a directory entry if it's available in-memory.
      const dirEntry = (vendors || []).find(x => x.id === vid);
      if (dirEntry) {
        setLinkedVendor({
          id: dirEntry.id,
          name: dirEntry.name || '',
          type: dirEntry.type || '',
          vendorNumber: dirEntry.vendorNumber || '',
          city: dirEntry.city || '',
          state: dirEntry.state || '',
        });
        setLinkedVendorError(null);
        setLinkedVendorLoading(false);
        // mark that we have directory-backed data — but don't override
        // a previously-loaded protected vendor view (admins). Respect
        // explicit role indicating the profile should show protected data.
        if (!vendorLinkedFromDirectory && profile.vendorRole !== 'admin' && !protectedVendorLoaded.current) setVendorLinkedFromDirectory(true);
        return;
      }

      // If the directory is still loading, wait for it to finish so we can
      // prefer directory data and avoid protected reads.
      if (vendorLoading) {
        // leave linkedVendorLoading as-is; effect will re-run when `vendors` updates
        return;
      }

      try {
        setLinkedVendorLoading(true);
        setLinkedVendorError(null);
        const v = await getVendor(vid);
        if (!mounted) return;
        if (v) {
          setLinkedVendor({
            id: v.id,
            name: v.name || v.businessName || '',
            type: v.type || v.businessType || '',
            vendorNumber: v.vendorNumber || '',
            city: v.addressCity || v.businessAddressCity || v.city || '',
            state: v.addressState || v.businessAddressState || v.state || '',
          });
        } else {
          setLinkedVendor(null);
        }
      } catch (e) {
        console.warn('Failed to load linked vendor info:', e?.message || e);
        if (!mounted) return;
        setLinkedVendorError(e?.message || String(e));
        setLinkedVendor(null);
      } finally {
        if (!mounted) return;
        setLinkedVendorLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [profile.vendorId, vendorLinkedFromDirectory, vendors]);

  // Vendor admin member listing/approval moved to Home.jsx (centralized)

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'businessName') {
      const sanitized = String(value || '').replace(/_/g, '');
      setProfile(prev => ({ ...prev, [name]: sanitized }));
      return;
    }
    if (name === 'phoneNumber' || name === 'businessPhone') {
      const normalizedPhone = normalizeLocalPhoneInput(value);
      setProfile(prev => ({ ...prev, [name]: normalizedPhone }));
      return;
    }
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const normalizeCountryCodeInput = (value) => {
    const raw = String(value || '').trim();
    const digits = raw.replace(/\D/g, '').slice(0, 3);
    // Keep a leading '+' at all times so browser autofill like "1" becomes "+1".
    return digits ? `+${digits}` : '+';
  };

  const normalizeLocalPhoneInput = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    // Common autofill case: includes US country code prefix "1".
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.slice(1);
    }
    return digits.slice(0, 10);
  };

  // Keep business contact in sync with user contact when toggled
  useEffect(() => {
    if (profile.businessUseUserEmail) {
      setProfile(prev => ({ ...prev, businessEmail: prev.email }));
    }
  }, [profile.email, profile.businessUseUserEmail]);

  useEffect(() => {
    // When the user checks "Same as user phone", copy both the user's
    // area/country code and the user's phone digits into the business fields.
    if (profile.businessPhoneNumber) {
      setProfile(prev => ({ ...prev, businessPhone: (prev.phoneNumber ?? prev.phone) || '' }));
      try { setBusinessAreaCode(userAreaCode); } catch (_) {}
    }
  }, [profile.businessPhoneNumber, profile.phoneNumber, profile.phone, userAreaCode]);

  // Cleanup success message timer on unmount
  useEffect(() => {
    return () => {
      if (successTimer.current) clearTimeout(successTimer.current);
    };
  }, []);

  useEffect(() => {
    if (error) setShowErrorPopup(true);
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !targetUid) return;

    const isDuplicateVendorNameError = (errObj) => {
      const code = String(errObj?.code || '').trim().toLowerCase();
      const msg = String(errObj?.message || '').trim().toLowerCase();
      return code === 'duplicate-vendor-name' || msg.includes('business name already exists');
    };

    const showDuplicateVendorNamePopup = () => {
      setDuplicateNamePopupMessage('A vendor with this business name already exists. You must use a different name or link your profile with the existing busuiness record, if you are part of the organization.');
      setShowDuplicateNamePopup(true);
    };

    setShowDuplicateNamePopup(false);
    setDuplicateNamePopupMessage('');

    // Require acknowledgement of Terms & Cancellation Policy before proceeding
    if (!profile.acknowledgedPolicies) {
      setError('You must acknowledge the Terms and Conditions and Cancellation Policy before saving your profile.');
      return;
    }
    // Only for self-edits: Anonymous users must provide a valid email to save profile data
    if (!editingOtherUser && isAnonymous) {
      const email = (profile.email || '').trim();
      const valid = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email);
      if (!valid) {
        setError('Please provide a valid email to save your profile.');
        return;
      }
      if (password.length < 6) {
        setError('Please set a password with at least 6 characters to upgrade your account.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    if (!profile.gender || (profile.gender !== 'Male' && profile.gender !== 'Female')) {
      setError('Please select Gender.');
      return;
    }

    const yobRaw = String(profile.yob || '').trim();
    if (!/^\d{4}$/.test(yobRaw)) {
      setError('YOB is required and must be exactly 4 digits.');
      return;
    }
    const yobYear = Number(yobRaw);
    const currentYear = new Date().getFullYear();
    if (yobYear < 1900 || yobYear > currentYear) {
      setError(`YOB must be between 1900 and ${currentYear}.`);
      return;
    }

    // Vendor linking/creation is intentionally independent from profile save.
    // Users should be able to save personal details first, then link/create.

    // Validate phone (if provided) and prepare payload
    let payload = { ...profile };
    // Phone is required for profile saves — validate and normalize
    if (!profile.phoneNumber) {
      setPhoneError('Phone is required.');
      return;
    }
    // Strip non-digit characters from the entered phone and require 10 digits
    const digits = normalizeLocalPhoneInput(profile.phoneNumber || '');
    if (digits.length !== 10) {
      setPhoneError('Phone number must be 10 digits.');
      return;
    }
    if (digits[0] === '0') {
      setPhoneError('Phone number cannot start with 0.');
      return;
    }
    // Validate userAreaCode format: leading + and 1-3 digits
    if (!/^\+\d{1,3}$/.test(userAreaCode)) {
      setPhoneError('Country code must start with + and contain 1-3 digits (e.g. +1).');
      return;
    }
    setPhoneError('');
    // Store phone as the 10-digit local number and persist the separate
    // `userAreaCode` field on the profile document. Write both `phone`
    // and `phoneNumber` to be compatible with UI variants that renamed
    // the field to `phoneNumber`.
    payload.phone = digits;
    payload.phoneNumber = digits;
  // Business handling: If the user supplied a business name, first check
    // whether that vendor already exists. If it does, associate the profile
    // to the existing vendor (no vendor details are copied). If it does not
    // exist, require full vendor info to create a new vendor and make the
    // user the vendor admin.
    const desiredBusinessName = (profile.businessName || '').trim();
    // Linked members should not mutate vendor business details from Profile page.
    const hasBusiness = !isLinkedReadOnlyBusiness && desiredBusinessName.length > 0;
    if (hasBusiness) {
      if (desiredBusinessName.length > 30) {
        setError('Business name must be 30 characters or fewer.');
        return;
      }
      if (desiredBusinessName.includes('_')) {
        setError("Business name cannot include underscore (_). Use letters, numbers, spaces, and these symbols: . - ' &.");
        return;
      }
      if (!/^[A-Za-z0-9 .'&\-]+$/.test(desiredBusinessName)) {
        setError("Business name can contain only English letters, numbers, spaces, and these symbols: . - ' &.");
        return;
      }
      // If a vendor is already selected (vendorId present), treat this as
      // linking an existing vendor and skip the creation/validation checks.
      if (profile.vendorId) {
        // nothing to validate here — linking will happen below
      } else {
        // Check if a vendor with this name already exists
        let foundVendor = null;
        try {
          foundVendor = await findVendorByName(desiredBusinessName, profile.vendorId);
        } catch (e) {
          // If permission denied or other errors happen, treat as not found and
          // allow the normal create flow to surface errors later.
          if (!(e?.code === 'permission-denied' || e?.message?.includes('insufficient permissions'))) {
            console.error('Error checking for existing vendor by name:', e);
          }
          foundVendor = null;
        }

        if (foundVendor) {
          // Associate to existing vendor. Do not copy business fields into profile.
        } else {
          // No existing vendor found: validate full vendor details are present (creation case)
          const be = (profile.businessEmail || '').trim();
          if (!/[^\s@]+@[^\s@]+\.[^\s@]+/.test(be)) {
            setError('Business email is required and must be valid when creating a new vendor.');
            return;
          }
          // Phone required and valid
          if (profile.businessPhone) {
            if (country && country !== 'OTHER') {
              const bp = parsePhoneNumberFromString(profile.businessPhone, country);
              if (!bp || !bp.isValid()) {
                setBizPhoneError('Please enter a valid business phone number.');
                return;
              }
              setBizPhoneError('');
            } else {
              setBizPhoneError('');
            }
          } else {
            setError('Business phone is required when creating a new vendor.');
            return;
          }
          // Address required
          const isOtherCountry = country === 'OTHER';
          if (!profile.businessAddressStreet || !profile.businessAddressCity) {
            setError('Business address (Street and City) is required when creating a new vendor.');
            return;
          }
          if (!isOtherCountry && (!profile.businessAddressState || !profile.businessAddressZip)) {
            setError('Business State and ZIP are required for the selected country.');
            return;
          }
          if (isOtherCountry && !(profile.businessCountryName || '').trim()) {
            setError('Please specify the country name for Other.');
            return;
          }
          // Type required
          if (!profile.businessType) {
            setError('Business type is required when creating a new vendor.');
            return;
          }
        }
      }
    }
    // Use the original vendorId loaded from the server to determine whether
    // this save creates a new vendor association. `profile.vendorId` may
    // already reflect the user's UI selection, so consult `originalVendorId`.
    // IMPORTANT: do not fallback to `profile.vendorId` here — that would
    // make the previous value equal the newly-selected value and prevent
    // detecting a new association. Use the stored original value (may be
    // empty string) so we can correctly detect changes.
    const prevVendorId = originalVendorId.current;

    try {
        // Preflight: ensure auth ready and user has permission to perform requested actions
        
        if (!currentUser || !currentUser.uid) {
          setError('You must be signed in to save your profile.');
          return;
        }

        // If editing another user's profile, ensure current user is an admin/organizer
        if (editingOtherUser && currentUser.uid !== targetUid) {
          // Attempt to check current user's profile for admin status
          try {
            const me = await getUserProfile(currentUser.uid);
            const isAdmin = me && (me.userType === 'Admin' || me.userType === 'Organizer');
            if (!isAdmin) {
              setError('You are not authorized to edit another user. Only admins/organizers may edit other profiles.');
              return;
            }
          } catch (e) {
            // If we can't determine admin status, fail safe and block the operation
            console.warn('Could not verify admin status before profile save', e);
            setError('Unable to verify permissions to edit another user. Try signing out and signing in again.');
            return;
          }
        }

        setSaving(true);
      setError(null);
      // For self-edits: If anonymous, upgrade account by linking email+password
      if (!editingOtherUser && isAnonymous) {
        try {
          const cred = EmailAuthProvider.credential(profile.email.trim(), password);
          await linkWithCredential(auth.currentUser, cred);
        } catch (err) {
          if (err?.code === 'auth/email-already-in-use') {
            // Email is already registered; guide the user to sign in instead.
            setError('This email is already in use. Please sign in with this email and password to merge your data.');
            setSaving(false);
            return;
          }
          setError(err.message || 'Failed to upgrade anonymous account.');
          setSaving(false);
          return;
        }
      }
      // Business phone is canonical on the vendor document. Do not write
      // business phone into the profile. Prepare digits here for vendor
      // create/update flows below instead of persisting to `profiles`.
      const bpDigits = profile.businessPhone ? (profile.businessPhone || '').replace(/\D/g, '') : null;
      // Determine vendor association/create behavior
      // Preserve existing vendorId when no business info is provided (avoid clearing accidentally)
      let vendorIdRef = (profile.vendorId || '').trim();
      let createdVendor = false;
      if (hasBusiness && !vendorIdRef) {
        const desiredBusinessName = (profile.businessName || '').trim();
        // Re-check existing vendor by name (non-exceptional path handled above)
        let existingVendor = null;
        try {
          existingVendor = await findVendorByName(desiredBusinessName, profile.vendorId);
        } catch (e) {
          // If permission denied or other read errors happen, treat as not found
          // and allow the normal create flow to surface errors later. Log
          // unexpected errors so debugging info is visible in console.
          if (!(e?.code === 'permission-denied' || e?.message?.toLowerCase?.().includes('insufficient permissions'))) {
            console.error('Error checking for existing vendor by name (re-check):', e);
          } else {
            console.warn('Permission denied when re-checking vendor by name; proceeding to creation to surface the error', e?.code || e?.message || e);
          }
          existingVendor = null;
        }

        if (existingVendor) {
          // Associate to existing vendor (do not copy vendor details into profile)
          vendorIdRef = existingVendor.id;
        } else {
          // Create new vendor and make this user the vendor admin
          const vendorPayload = {
            name: profile.businessName,
            type: profile.businessType,
            email: profile.businessEmail,
            // New canonical vendor phone shape: store `phoneNumber` (local digits)
            // and `countryCode` (prefix like +1). Do not store an E.164 `phone` string.
            phoneNumber: (bpDigits && bpDigits.length === 10 && bpDigits[0] !== '0') ? bpDigits.slice(-10) : (bpDigits ? bpDigits : ''),
            businessAreaCode: businessAreaCode || '+1',
            description: profile.businessDescription || '',
            federalId: profile.federalId || '',
            addressStreet: profile.businessAddressStreet,
            addressCity: profile.businessAddressCity,
            addressState: profile.businessAddressState,
            addressZip: profile.businessAddressZip,
            countryName: country === 'OTHER' ? (profile.businessCountryName || '') : '',
            vendorStatus: profile.vendorStatus || 'Active',
            // Make the saving user the initial member/admin
            // Ensure the authenticated user is included so Firestore create rules pass
            memberUids: Array.from(new Set([targetUid, currentUser?.uid].filter(Boolean))),
            createdByUid: currentUser?.uid || targetUid,
            // Prefer making the profile owner the vendor admin; if current user is creating for self this is equivalent
            vendorAdmin: targetUid || currentUser?.uid,
          };

          // Log vendor payload and auth context for debugging permission issues
          

          let v = null;
          try {
            v = await upsertVendor(null, vendorPayload);
            
          } catch (err) {
            console.error('upsertVendor failed', err);
            if (isDuplicateVendorNameError(err)) {
              showDuplicateVendorNamePopup();
              setSaving(false);
              return;
            }
            setError(`Vendor creation failed: ${err?.code || ''} ${err?.message || err}`);
            setSaving(false);
            return;
          }
          if (v && v.__error) {
            console.error('upsertVendor returned error object', v.__error);
            if (isDuplicateVendorNameError(v.__error)) {
              showDuplicateVendorNamePopup();
              setSaving(false);
              return;
            }
            setError(`Vendor creation failed: ${v.__error.code} ${v.__error.message}`);
            setSaving(false);
            return;
          }
          // Diagnostic: ensure vendor creation returned an id
          if (!v || !v.id) {
            console.error('upsertVendor did not return created vendor id', v);
            setError('Failed to create vendor record (no id returned). Check console for details.');
            setSaving(false);
            return;
          }
          vendorIdRef = v.id;
          createdVendor = true;
        }
      }

    // If editing an existing vendor's fields (user is vendor admin or an admin editor),
    // persist those vendor changes to the `vendors` collection so fields like
    // `federalId` are saved in the canonical vendor doc. We only do this when
    // we did not just create a vendor (createdVendor === false) and when a
    // vendorIdRef exists.
    const isVendorAdminNow = currentUser && profile.vendorAdmin && String(profile.vendorAdmin) === String(currentUser.uid);
    const canPersistVendorEdits = !isLinkedReadOnlyBusiness && vendorIdRef && !createdVendor && (isVendorAdminNow || fromAdmin || editingOtherUser);
    if (canPersistVendorEdits) {
      const vendorUpdatePayload = {
        name: profile.businessName,
        type: profile.businessType,
        email: profile.businessEmail,
        phoneNumber: (bpDigits && bpDigits.length === 10 && bpDigits[0] !== '0') ? bpDigits.slice(-10) : (bpDigits ? bpDigits : undefined),
        businessAreaCode: businessAreaCode || '+1',
        description: profile.businessDescription || '',
        federalId: profile.federalId || undefined,
        addressStreet: profile.businessAddressStreet,
        addressCity: profile.businessAddressCity,
        addressState: profile.businessAddressState,
        addressZip: profile.businessAddressZip,
        countryName: country === 'OTHER' ? (profile.businessCountryName || '') : undefined,
      };
      // Remove explicit undefined keys so upsertVendor sees only provided fields
      Object.keys(vendorUpdatePayload).forEach(k => vendorUpdatePayload[k] === undefined && delete vendorUpdatePayload[k]);

      try {
        const updatedVendor = await upsertVendor(vendorIdRef, vendorUpdatePayload);
        
        if (updatedVendor && updatedVendor.__error) {
          console.error('upsertVendor returned error object', updatedVendor.__error);
          if (isDuplicateVendorNameError(updatedVendor.__error)) {
            showDuplicateVendorNamePopup();
            setSaving(false);
            return;
          }
          setError(`Vendor update failed: ${updatedVendor.__error.code} ${updatedVendor.__error.message}`);
          setSaving(false);
          return;
        }
      } catch (err) {
        console.error('upsertVendor (update) failed', err);
        if (isDuplicateVendorNameError(err)) {
          showDuplicateVendorNamePopup();
          setSaving(false);
          return;
        }
        setError(`Vendor update failed: ${err?.code || ''} ${err?.message || err}`);
        setSaving(false);
        return;
      }
    }

    // userStatus stays as provided in the payload; no automatic promotion needed
    const userStatusToSave = payload.userStatus;

    const profilePayload = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      gender: payload.gender,
      yob: String(payload.yob || '').trim(),
      email: payload.email,
      phoneNumber: payload.phoneNumber,
      userType: payload.userType,
      userStatus: userStatusToSave,
      vendorId: vendorIdRef || '',
      // Note: business contact is stored on the vendor document, not on profiles
    };
    // Persist acknowledgement and countryCode so the user doesn't have to re-check
    // and so the country/area code is stored separately from the 10-digit phone.
    profilePayload.acknowledgedPolicies = !!profile.acknowledgedPolicies;
    // Persist the user's phone prefix under `userAreaCode` on the profile
    profilePayload.userAreaCode = userAreaCode || '+1';
    profilePayload.updatedBy = currentUser?.uid || '';
    profilePayload.updatedByName =
      `${myProfile?.firstName || ''} ${myProfile?.lastName || ''}`.trim() ||
      myProfile?.name ||
      currentUser?.displayName ||
      myProfile?.email ||
      currentUser?.email ||
      currentUser?.uid ||
      '';
  // Set vendorRole: 'admin' when we created the vendor now, 'user' when linking to existing vendor
  if (vendorIdRef) {
    if (createdVendor) {
      profilePayload.vendorRole = 'admin';
    } else {
      // Preserve an existing admin role when saving the profile. If the
      // profile previously referenced the same vendor and had vendorRole
      // set to 'admin', keep that role. Otherwise treat the association as
      // a regular 'user' role.
      try {
        const prevIsSameVendor = prevVendorId && String(prevVendorId) === String(vendorIdRef);
        const wasAdmin = prevIsSameVendor && profile.vendorRole === 'admin';
        profilePayload.vendorRole = wasAdmin ? 'admin' : 'user';
      } catch (roleErr) {
        profilePayload.vendorRole = 'user';
      }
    }
    // If we are linking to an existing vendor (not creating one) and this is
    // a new vendor association (different from previous), mark the link as
    // pending approval so vendor admins can approve it later.
    try {
      const prevIsSameVendor = prevVendorId && String(prevVendorId) === String(vendorIdRef);
      if (!createdVendor && !prevIsSameVendor) {
        profilePayload.linkApproval = 'pending';
      }
    } catch (linkErr) {
      // non-fatal
    }
  }

  // Diagnostic removed
  // Defensive: if we created the vendor in this flow, ensure the outgoing
  // payload includes vendorRole: 'admin' so server rules receive the intent.
  if (createdVendor) profilePayload.vendorRole = 'admin';

  let saved = null;
  try {
    saved = await upsertUserProfile(targetUid, profilePayload);
    
    if (saved && saved.__error) {
      console.error('upsertUserProfile returned error object', saved.__error);
      setError(`Profile save failed: ${saved.__error.code} ${saved.__error.message}`);
      setSaving(false);
      return;
    }
  } catch (err) {
    console.error('upsertUserProfile failed', err);
    setError(`Profile save failed: ${err?.code || ''} ${err?.message || err}`);
    setSaving(false);
    return;
  }
  setProfile(prev => ({ ...prev, ...saved }));
  setHasPersistedProfile(true);
  // update originalVendorId so subsequent edits compare against latest persisted value
  try { originalVendorId.current = saved && saved.vendorId ? saved.vendorId : ''; } catch (_) {}
  // Keep local userAreaCode state in sync with persisted value (if returned)
  try {
    const persistedUserCode = (saved && (saved.userAreaCode ?? saved.countryCode));
    if (persistedUserCode) setUserAreaCode(persistedUserCode);
  } catch (_) {}
  // Diagnostic: ensure server persisted the vendorId we attempted to set
  if (profilePayload.vendorId && saved && saved.vendorId !== profilePayload.vendorId) {
    console.error('Profile upsert did not persist vendorId', { attempted: profilePayload.vendorId, saved });
    setError('Profile saved but vendor link was not persisted. Check Firestore rules or try again.');
    // Continue but do not navigate away so user can retry
    setSaving(false);
    return;
  }
  // If the saved profile does not have `vendorRole: 'admin'` but we
  // intended/promised to create an admin (either because we created the
  // vendor in this flow or because the outgoing payload asked for admin),
  // attempt a targeted upsert to enforce the admin role.
  try {
    const shouldBeAdmin = !!vendorIdRef && (createdVendor || (profilePayload && profilePayload.vendorRole === 'admin'));
    if (shouldBeAdmin && !(saved && saved.vendorRole === 'admin')) {
      try {
        const corrected = await upsertUserProfile(targetUid, { vendorRole: 'admin', vendorId: vendorIdRef });
        if (corrected && !corrected.__error) {
          setProfile(prev => ({ ...prev, ...corrected }));
        }
      } catch (e) {
        console.warn('Failed to enforce vendorRole=admin after save', e);
      }
    }
  } catch (e) {
    // non-fatal
  }
  // Verify server returned persisted core fields; if not, surface an error
    try {
    const persistedOk = (
      (profilePayload.firstName === undefined || saved.firstName === profilePayload.firstName) &&
      (profilePayload.lastName === undefined || saved.lastName === profilePayload.lastName) &&
      (profilePayload.email === undefined || saved.email === profilePayload.email) &&
      // Profiles service persists `phoneNumber`; fall back to legacy `phone` if present
      (profilePayload.phoneNumber === undefined || ((saved.phoneNumber ?? saved.phone) === profilePayload.phoneNumber))
    );
    if (!persistedOk) {
      setError('Profile save did not persist core fields. Please check Firestore rules or try again.');
      setSaving(false);
      return;
    }
  } catch (verr) {
    // Non-fatal — continue
  }
  // If we newly linked a vendor during this save, show a transient success message
  try {
    if (vendorIdRef && vendorIdRef !== prevVendorId) {
      const dirEntry = (vendors || []).find(x => x.id === vendorIdRef);
      const vendorName = dirEntry ? dirEntry.name : (profile.businessName || 'Vendor');
      if (successTimer.current) clearTimeout(successTimer.current);
      setSuccessMessage(`Vendor linked: ${vendorName}`);
      successTimer.current = setTimeout(() => setSuccessMessage(''), 4000);
    }
  } catch (msgErr) {
    // non-fatal; don't block navigation
    console.warn('Failed setting success message', msgErr);
  }
  // Best-effort notification: when a user requests linking to an existing vendor,
  // email the vendor admin/business contact with requester details.
  try {
    const requestedVendorLink = !createdVendor && becameLinked && profilePayload.linkApproval === 'pending' && !!vendorIdRef;
    if (requestedVendorLink) {
      const requesterName = `${profilePayload.firstName || ''} ${profilePayload.lastName || ''}`.trim() || currentUser?.displayName || 'A user';
      const requesterEmail = (profilePayload.email || currentUser?.email || '').trim();
      const vendorRow = (vendors || []).find(v => String(v?.id || '') === String(vendorIdRef || ''));
      const vendorLabel = vendorRow?.name || profile.businessName || vendorIdRef;

      let toEmail = String(vendorRow?.email || '').trim();
      try {
        if (!toEmail) {
          const vendorDoc = await getVendor(vendorIdRef);
          toEmail = String(vendorDoc?.email || '').trim();
        }
      } catch (_) {
        // ignore: best-effort fallback above may already have a value
      }

      if (toEmail) {
        const subject = `Vendor Access Request: ${vendorLabel}`;
        const body = `
          <p>Hello,</p>
          <p>A user requested access to your vendor account.</p>
          <p><strong>Vendor:</strong> ${vendorLabel}</p>
          <p><strong>Requester Name:</strong> ${requesterName}</p>
          <p><strong>Requester Email:</strong> ${requesterEmail || '-'}</p>
          <p>Please sign in and review this request from your Vendor Team section.</p>
        `;
        await sendEmail({ to: toEmail, subject, type: 'HTML', body });
      } else {
        console.warn('Link request email skipped: vendor email is not available', { vendorId: vendorIdRef, vendorLabel });
      }
    }
  } catch (notifyErr) {
    console.warn('Failed to send vendor link request email (non-blocking):', notifyErr?.message || notifyErr);
  }

  // Stay on the profile page after save so users can continue to the
  // business/link step without being redirected away.
  if (successTimer.current) clearTimeout(successTimer.current);
  if (!vendorIdRef) {
    setSuccessMessage('Profile saved. You can now create a business record or link to an existing vendor.');
  } else {
    setSuccessMessage('Profile saved successfully.');
  }
  successTimer.current = setTimeout(() => setSuccessMessage(''), 5000);
    } catch (e) {
      console.error('Save failed', e);
      setError(e.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) {
    return <div className="profile-container profile-wide-container">You must be logged in to view your profile.</div>;
  }

  if (loading) {
    return <div className="profile-container profile-wide-container">Loading profile…</div>;
  }

  const canEditUserMeta = editingOtherUser || profile.userType === 'Admin' || profile.userType === 'Organizer';
  const showVendorStep = editingOtherUser || hasPersistedProfile || Boolean(profile.vendorId);
  const showVendorActionChooser = showVendorStep && !isLinkedReadOnlyBusiness && !profile.vendorId && vendorSetupMode === '';
  const canEditVendorFields = showVendorStep && ((!profile.vendorId && vendorSetupMode === 'create') || (Boolean(profile.vendorId) && profile?.vendorRole === 'admin'));
  

  return (
  <div className="profile-container profile-wide-container">
      <div className="profile-header-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          className="back-btn"
          aria-label="Go back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="apply-btn"
          aria-label="Save"
          onClick={() => formRef.current?.requestSubmit && formRef.current.requestSubmit()}
          disabled={saving}
          style={{ minWidth: 110 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="cancel-btn"
          aria-label="Cancel"
          onClick={() => goAfterDone()}
          style={{ minWidth: 88 }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="cancel-btn"
          aria-label="Close"
          onClick={() => goAfterDone()}
          style={{ minWidth: 88 }}
        >
          Close
        </button>
      </div>
      <h2 className="profile-title">User Profile</h2>
      {/* Admin Section - only visible to admins/organizers */}
        {canEditUserMeta && (
          <>
            <h2 className="profile-title">Admin Section</h2>
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                backgroundColor: '#f9fafb',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Current Event Role Assignments</div>
              {roleEventsLoading && <div className="profile-hint">Loading event assignments...</div>}
              {roleEventsError && <div className="profile-error" style={{ marginBottom: 8 }}>{roleEventsError}</div>}
              {!roleEventsLoading && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Organizer for Events ({organizerEvents.length})</div>
                    {organizerEvents.length === 0 ? (
                      <div className="profile-hint">No organizer event assignments.</div>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {organizerEvents.map(ev => (
                          <li key={ev.id}>{ev.name || ev.id}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>EventAdmin for Events ({eventAdminEvents.length})</div>
                    {eventAdminEvents.length === 0 ? (
                      <div className="profile-hint">No event admin assignments.</div>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {eventAdminEvents.map(ev => (
                          <li key={ev.id}>{ev.name || ev.id}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="profile-grid">
              <div>
                <label className="profile-label">User Type</label>
                <select name="userType" value={profile.userType} onChange={handleChange} className="profile-input" disabled={!isCurrentUserAdmin}>
                  {userTypeOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="profile-label">User Status</label>
                <select name="userStatus" value={profile.userStatus} onChange={handleChange} className="profile-input">
                  {userStatusOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              {(profile.userType === 'Vendor' || profile.userType === 'Admin' || profile.userType === 'Organizer') && (
                <div>
                  <label className="profile-label">Vendor Status</label>
                  <select 
                    name="vendorStatus" 
                    value={profile.vendorStatus} 
                    onChange={handleChange} 
                    className="profile-input"
                  >
                    {vendorStatusOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              )}
              {(profile.userType === 'Vendor' || profile.userType === 'Admin' || profile.userType === 'Organizer') && profile.vendorNumber && profile.vendorAdmin && (
                <div>
                  <label className="profile-label">Vendor Admin</label>
                  <input 
                    value={vendorAdminDisplayName || profile.vendorAdmin} 
                    className="profile-input" 
                    readOnly 
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                  <div className="profile-hint">The user who created this vendor record. UID: {profile.vendorAdmin}</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* User Profile section */}
        <h2 className="profile-title" style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          User Profile
          {profile.userStatus && (
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: '4px',
              backgroundColor: 
                profile.userStatus === 'Active' ? '#10b981' :
                profile.userStatus === 'Inactive' ? '#6b7280' :
                profile.userStatus === 'Suspended' ? '#ef4444' : '#9ca3af',
              color: '#ffffff'
            }}>
              {profile.userStatus}
            </span>
          )}
        </h2>
        <form ref={formRef} onSubmit={handleSubmit}>
        <div className="profile-grid">
          <div>
            <label className="profile-label">Email {!editingOtherUser && isAnonymous && <span style={{color:'#c00'}}>*</span>}</label>
            <input 
              type="email"
              name="email"
              value={profile.email}
              onChange={e => setProfile(prev => ({ ...prev, email: e.target.value }))}
              className="profile-input" 
              readOnly={!(!editingOtherUser && isAnonymous)}
              placeholder={!editingOtherUser && isAnonymous ? 'you@example.com' : ''}
            />
            {!editingOtherUser && isAnonymous && (
              <div className="profile-hint">Anonymous users must provide an email to store profile data.</div>
            )}
          </div>
          {!editingOtherUser && isAnonymous && (
            <>
              <div>
                <label className="profile-label">Set Password <span style={{color:'#c00'}}>*</span></label>
                <input type="password" className="profile-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" />
              </div>
              <div>
                <label className="profile-label">Confirm Password <span style={{color:'#c00'}}>*</span></label>
                <input type="password" className="profile-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              </div>
            </>
          )}
          <div>
            <div className="phone-row-flex">
              <div className="col-fixed">
                <label className="profile-label">Country Code</label>
                <input
                  name="userAreaCode"
                  value={userAreaCode}
                  onChange={e => setUserAreaCode(normalizeCountryCodeInput(e.target.value))}
                  onBlur={e => setUserAreaCode(normalizeCountryCodeInput(e.target.value))}
                  className="profile-input phone-area-code"
                  placeholder="+1"
                  style={{ width: 116 }}
                  aria-label="Country code"
                  autoComplete="off"
                  required
                />
              </div>
              <div className="col-grow">
                <label className="profile-label">Phone Number</label>
                <input name="phoneNumber" value={profile.phoneNumber} onChange={handleChange} className="profile-input phone-number" placeholder="e.g. 5555551234" autoComplete="tel-national" required />
              </div>
            </div>
            {phoneError && <div className="profile-error" style={{marginTop: 6}}>{phoneError}</div>}
          </div>
        </div>
        <div className="two-col-row">
          <div>
            <label className="profile-label">First Name</label>
            <input name="firstName" value={profile.firstName} onChange={handleChange} className="profile-input" />
          </div>
          <div>
            <label className="profile-label">Last Name</label>
            <input name="lastName" value={profile.lastName} onChange={handleChange} className="profile-input" />
          </div>
          <div>
            <label className="profile-label">Gender <span style={{color:'#c00'}}>*</span></label>
            <select name="gender" value={profile.gender || ''} onChange={handleChange} className="profile-input" required>
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div>
            <label className="profile-label">YOB <span style={{color:'#c00'}}>*</span></label>
            <input
              name="yob"
              value={profile.yob || ''}
              onChange={e => setProfile(prev => ({ ...prev, yob: (e.target.value || '').replace(/\D/g, '').slice(0, 4) }))}
              className="profile-input"
              placeholder="e.g. 1990"
              inputMode="numeric"
              maxLength={4}
              required
            />
            <div className="profile-hint">Enter 4-digit year between 1900 and current year.</div>
          </div>
        </div>
        {/* Business Information section */}
        {showVendorStep ? (
        <>
        <h2 className="profile-title" style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          Business Information
          {(profile.userType === 'Vendor' || profile.userType === 'Admin' || profile.userType === 'Organizer') && profile.vendorStatus && (
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: '4px',
              backgroundColor: 
                profile.vendorStatus === 'Active' ? '#10b981' :
                profile.vendorStatus === 'Inactive' ? '#6b7280' :
                profile.vendorStatus === 'Blocked' ? '#ef4444' : '#9ca3af',
              color: '#ffffff'
            }}>
              {profile.vendorStatus}
            </span>
          )}
        </h2>
        <div className="profile-grid">
          {/* ── LINKED MEMBER VIEW (vendorRole='user'): read-only only ────────────────────── */}
          {isLinkedReadOnlyBusiness && (
            <div style={{ gridColumn: '1 / -1' }}>
              {/* Linked vendor summary row */}
              <div style={{ marginBottom: 8 }}>
                <label className="profile-label">Linked Organization</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    value={`${linkedVendor?.name || profile.businessName || ''}${(linkedVendor?.name || profile.businessName) ? ' — ' : ''}${linkedVendor?.vendorNumber || profile.vendorNumber || profile.vendorId || ''}`}
                    readOnly
                    className="profile-input"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                  {profile.linkApproval !== 'approved' && (
                    <button type="button" className="cancel-btn" onClick={handleUnlinkVendor}>Unlink</button>
                  )}
                </div>
              </div>
              {/* Linked vendor detail card */}
              {(linkedVendor || linkedVendorLoading) && (
                <div style={{ marginBottom: 12, padding: 12, border: '1px solid #eef2f0', borderRadius: 8, background: '#fbfdfd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontWeight: 700 }}>Organization Info</div>
                    <div style={{ fontSize: 13, color: '#556' }}>{linkedVendorLoading ? 'Loading…' : linkedVendorError ? `Failed to load: ${linkedVendorError}` : ''}</div>
                  </div>
                  {linkedVendor && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      <div><label className="profile-label">Business Name</label><div className="profile-input" style={{ padding: '8px 12px', background: '#fff' }}>{linkedVendor.name}</div></div>
                      <div><label className="profile-label">Type</label><div className="profile-input" style={{ padding: '8px 12px', background: '#fff' }}>{linkedVendor.type}</div></div>
                      <div><label className="profile-label">Vendor #</label><div className="profile-input" style={{ padding: '8px 12px', background: '#fff' }}>{linkedVendor.vendorNumber}</div></div>
                      <div><label className="profile-label">City</label><div className="profile-input" style={{ padding: '8px 12px', background: '#fff' }}>{linkedVendor.city}</div></div>
                      <div><label className="profile-label">State</label><div className="profile-input" style={{ padding: '8px 12px', background: '#fff' }}>{linkedVendor.state}</div></div>
                    </div>
                  )}
                </div>
              )}
              {/* Status banners */}
              {profile.linkApproval === 'pending' && (
                <div style={{ marginBottom: 12, padding: 12, border: '1px solid #fde8d6', borderRadius: 8, background: '#fff7f0' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Link Pending Approval</div>
                  <div style={{ color: '#444' }}>Your request has been submitted and the vendor admin has been notified. Until the vendor organizer approves it, you will not be able to see vendor's data.</div>
                  <div style={{ color: '#444' }}>You can always unlink and create your own business record before thhis request is approved. Once approved, the admin must unlink you before you can change any thing.</div>
                </div>
              )}
              {profile.linkApproval === 'approved' && (
                <div style={{ marginBottom: 12, padding: 12, border: '1px solid #e6fffa', borderRadius: 8, background: '#f0fdf4' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Vendor Linked</div>
                  <div style={{ color: '#114b34' }}>Your account is linked to the above vendor. You can view and edit their booths and attendee information.</div>
                  <div style={{ color: '#114b34' }}>If you want to create your own vendor record, you must request the above vendor admin to unlink your account first and then you can add your own business record.</div>
                </div>
              )}
            </div>
          )}

          {/* ── NO VENDOR YET: choose create or link first ────────────────────── */}
          {showVendorActionChooser && (
            <div style={{ gridColumn: '1 / -1', marginBottom: 8 }}>
              <label className="profile-label">Business Setup</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <button type="button" className="apply-btn" onClick={startCreateVendor} style={{ whiteSpace: 'nowrap' }}>
                  Create a New Business Record
                </button>
                <button type="button" className="cancel-btn" onClick={startLinkVendor} style={{ whiteSpace: 'nowrap' }}>
                  Link to Existing One
                </button>
              </div>
            </div>
          )}

          {/* ── LINK MODE: search and request link ────────────────────── */}
          {!isLinkedReadOnlyBusiness && !profile.vendorId && vendorSetupMode === 'link' && (
            <div style={{ gridColumn: '1 / -1', marginBottom: 8 }}>
              <label className="profile-label">Link to Existing Vendor</label>
              <text className="profile-hint" style={{ marginBottom: 6, display: 'block', color: '#0f172a' }}>
                You can save your user profile first. Linking or creating a business record is a separate next step.
              </text>
              <text className="profile-hint" style={{ marginBottom: 6, display: 'block' }}>Search and link to an existing vendor record if your business is already registered. This way you will be able to see all reserved booths and attendee info.</text>    
              <text className="profile-hint" style={{ marginBottom: 6, display: 'block' }}>However, your request will have to be approved by the business organizer.</text>
              <text className="profile-hint" style={{ marginBottom: 6, display: 'block' }}>Otherwise, create a new vendor record and you will become its organizer.</text>
              {!(profile.firstName?.trim()) || !(profile.lastName?.trim()) ? (
                <div style={{ padding: '10px 14px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, color: '#92400e', fontSize: 14 }}>
                  Please enter your <strong>First Name</strong> and <strong>Last Name</strong> above and save your profile before linking to a vendor.
                </div>
              ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                  <button type="button" className="cancel-btn" onClick={() => { setVendorSetupMode(''); setDropdownOpen(false); }}>
                    Back
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={vendorQuery}
                  onChange={e => handleVendorQueryChange(e.target.value)}
                  onFocus={() => setDropdownOpen(true)}
                  onKeyDown={(e) => {
                    if (!dropdownOpen) setDropdownOpen(true);
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setHighlightIndex(i => Math.min((filteredVendors.length || 1) - 1, (i === -1 ? 0 : i + 1)));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightIndex(i => Math.max(0, (i === -1 ? 0 : i - 1)));
                    } else if (e.key === 'Enter') {
                      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
                        e.preventDefault();
                        handleSelectVendor(filteredVendors[highlightIndex].id);
                      }
                    } else if (e.key === 'Escape') {
                      setDropdownOpen(false);
                    }
                  }}
                  placeholder="Search vendors by name, type, vendor number, city or state"
                  className="profile-input"
                  style={{ flex: 1 }}
                />
                <button type="button" className="apply-btn" onClick={() => { setDebouncedQuery(vendorQuery.trim()); setDropdownOpen(true); }} style={{ whiteSpace: 'nowrap' }}>Search</button>
              </div>
              <div style={{ marginTop: 8, position: 'relative' }}>
                <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label className="profile-label" style={{ margin: 0 }}>Matches</label>
                  <div style={{ fontSize: 13, color: '#566270' }}>
                    {vendorLoading ? 'Loading vendors…' : vendorLoadError ? `Failed to load vendors: ${vendorLoadError}` : `${vendors.length} vendors available`} · {filteredVendors.length} matches
                  </div>
                </div>
                <div style={{ position: 'relative' }}>
                  {dropdownOpen && filteredVendors && filteredVendors.length > 0 ? (
                    <ul
                      className="vendor-dropdown"
                      style={{
                        position: 'absolute',
                        top: 6,
                        left: 0,
                        right: 0,
                        listStyle: 'none',
                        margin: 0,
                        padding: '6px 0',
                        maxHeight: 240,
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        background: '#fff',
                        zIndex: 1000,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
                      }}
                    >
                      {filteredVendors.map((v, idx) => (
                        <li
                          key={v.id}
                          onMouseDown={() => handleSelectVendor(v.id)}
                          onMouseEnter={() => setHighlightIndex(idx)}
                          style={{ padding: '8px 12px', cursor: 'pointer', background: idx === highlightIndex ? '#eef2ff' : 'transparent' }}
                        >
                          <div style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14, color: '#0f172a' }}>
                            {`${v.vendorNumber || ''}${v.vendorNumber ? ' — ' : ''}${v.name || ''}${v.name ? ' — ' : ''}${v.type || ''}${(v.city || v.state) ? ' — ' : ''}${v.city || ''}${v.city && v.state ? ', ' : ''}${v.state || ''}`}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ color: '#666' }}>{vendorLoading ? 'Loading…' : 'No matches.'}</div>
                      <button type="button" className="apply-btn" onClick={() => { setFilteredVendors(vendors || []); setVendorQuery(''); setDropdownOpen(true); }}>{vendors.length ? 'Show all vendors' : 'Reload vendors'}</button>
                    </div>
                  )}
                </div>
              </div>
              </>
              )}
            </div>
          )}

          {/* Show create/edit vendor fields only when creating or when user is vendor admin/admin editing */}
          {canEditVendorFields && (
            <div>
              <label className="profile-label">Business Name</label>
              <input name="businessName" value={profile.businessName} onChange={handleChange} className="profile-input" placeholder="Your company LLC" />
              {profile.vendorNumber && (
                <div className="profile-hint">Vendor #: {profile.vendorNumber}</div>
              )}
            </div>
          )}
          {canEditVendorFields && (
            <>
              <div>
                <label className="profile-label">Business Type</label>
                <select name="businessType" value={profile.businessType} onChange={handleChange} className="profile-input">
                  <option value="">Select…</option>
                  {businessTypes.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="profile-label">Business Description</label>
                <textarea
                  name="businessDescription"
                  value={profile.businessDescription}
                  onChange={handleChange}
                  className="profile-input"
                  rows={3}
                  placeholder="Describe your business (optional)"
                />
                <div className="profile-hint" style={{ marginTop: 6 }}>
                  Please provide more details for your business—what kind of clothing, what kind of non-profit, etc., to help admin understand your business.
                </div>
              </div>
              <div>
                <label className="profile-label">Business Email</label>
                    <input
                      type="email"
                      name="businessEmail"
                      value={profile.businessEmail}
                      onChange={handleChange}
                      className="profile-input"
                      placeholder="business@example.com"
                      disabled={profile.businessUseUserEmail}
                    />
                    <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, marginTop: 6 }}>
                      <input type="checkbox" checked={profile.businessUseUserEmail} onChange={e => setProfile(prev => ({ ...prev, businessUseUserEmail: e.target.checked }))} />
                      Same as user email
                    </label>
              </div>
              <div>
                <div className="phone-row-flex">
                  <div className="col-fixed">
                    <label className="profile-label">Country Code</label>
                      <input
                        name="businessAreaCode"
                        value={businessAreaCode}
                        onChange={e => setBusinessAreaCode(normalizeCountryCodeInput(e.target.value))}
                        onBlur={e => setBusinessAreaCode(normalizeCountryCodeInput(e.target.value))}
                        className="profile-input phone-area-code"
                        placeholder="+1"
                        style={{ width: 116 }}
                        aria-label="Country code"
                        autoComplete="off"
                      />
                  </div>
                  <div className="col-grow">
                    <label className="profile-label">Phone Number</label>
                    <input name="businessPhone" value={profile.businessPhone} onChange={handleChange} className="profile-input phone-number" placeholder="e.g. 5555556789" autoComplete="tel-national" disabled={profile.businessPhoneNumber} />
                  </div>
                </div>
                {bizPhoneError && <div className="profile-error" style={{marginTop: 6}}>{bizPhoneError}</div>}
                <label className="profile-label" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, marginTop: 6 }}>
                  <input type="checkbox" checked={profile.businessPhoneNumber} onChange={e => setProfile(prev => ({ ...prev, businessPhoneNumber: e.target.checked }))} />
                  Same as user phone
                </label>
              </div>
              <div>
                <label className="profile-label">Federal ID</label>
                <input
                  name="federalId"
                  value={profile.federalId}
                  onChange={handleChange}
                  className="profile-input"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="profile-label">Street</label>
                <input name="businessAddressStreet" value={profile.businessAddressStreet} onChange={handleChange} className="profile-input" />
              </div>
              <div>
                <label className="profile-label">City</label>
                <input name="businessAddressCity" value={profile.businessAddressCity} onChange={handleChange} className="profile-input" />
              </div>
              <div>
                <label className="profile-label">Country</label>
                <select className="profile-input" value={country} onChange={e => setCountry(e.target.value)}>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                {country === 'OTHER' && (
                  <div style={{ marginTop: 8 }}>
                    <label className="profile-label">Country Name (Other)</label>
                    <input
                      name="businessCountryName"
                      value={profile.businessCountryName}
                      onChange={e => setProfile(prev => ({ ...prev, businessCountryName: e.target.value }))}
                      className="profile-input"
                      placeholder="Enter country name"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="profile-label">State/Province</label>
                {stateCodeOptions[country] && country !== 'OTHER' ? (
                  <select
                    className="profile-input"
                    value={profile.businessAddressState}
                    onChange={e => setProfile(prev => ({ ...prev, businessAddressState: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {stateCodeOptions[country].map(s => (
                      <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input name="businessAddressState" value={profile.businessAddressState} onChange={handleChange} className="profile-input" />
                )}
              </div>
              <div>
                <label className="profile-label">ZIP</label>
                <input name="businessAddressZip" value={profile.businessAddressZip} onChange={handleChange} className="profile-input" placeholder={country === 'OTHER' ? 'Optional' : ''} />
              </div>
              {!profile.vendorId && (
                <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setVendorSetupMode('')}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Back
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        </>
        ) : (
          <div style={{ marginTop: 20, padding: 12, border: '1px solid #dbeafe', borderRadius: 8, background: '#eff6ff', color: '#1e3a8a' }}>
            Save your profile first. After saving, you will be able to create a business record or link to an existing vendor.
          </div>
        )}

        <div className="profile-actions" style={{ marginTop: 16 }}>
          <label className="profile-label" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontWeight: 500, marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={!!profile.acknowledgedPolicies}
              onChange={e => setProfile(prev => ({ ...prev, acknowledgedPolicies: e.target.checked }))}
              style={{ marginTop: 4 }}
            />
            <span>
              Acknowledge ICNA Convention Terms and Conditions, as well as the Cancellation Policy.
              {' '}
              <a href="https://icnaconvention.org/exhibitors/" target="_blank" rel="noopener noreferrer">Read policy</a>
            </span>
          </label>
        </div>

        <div className="profile-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
          {error && <div className="profile-error" style={{ marginRight: 12 }}>{error}</div>}
          {successMessage && <div className="profile-success" style={{ color: '#065f46', marginRight: 12 }}>{successMessage}</div>}
          <button type="submit" className="apply-btn" disabled={saving} style={{ whiteSpace: 'nowrap' }}>{saving ? 'Saving…' : 'Save Profile'}</button>
          <button type="button" className="cancel-btn" onClick={() => goAfterDone()}>Cancel</button>
        </div>

      </form>
          {/* Vendor member approval moved to Home.jsx — removed from ProfilePage */}
          <div className="profile-meta">
        {profile.createdAt && (<div>Created: {profile.createdAt?.toDate?.() ? profile.createdAt.toDate().toLocaleString() : new Date(profile.createdAt).toLocaleString()}</div>)}
        {profile.updatedAt && (
          <div>
            Updated: {profile.updatedAt?.toDate?.() ? profile.updatedAt.toDate().toLocaleString() : new Date(profile.updatedAt).toLocaleString()}
            {lastUpdatedByDisplayName ? ` By: ${lastUpdatedByDisplayName}` : ''}
          </div>
        )}
      </div>
          {showErrorPopup && !!error && (
            <div className="popup-overlay" onClick={() => setShowErrorPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
                <div className="popup-header">
                  <div style={{ fontSize: 18, fontWeight: 700 }}>Validation Error</div>
                </div>
                <div className="popup-body">
                  <div style={{ marginBottom: 12 }}>{error}</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button type="button" className="cancel-btn" onClick={() => setShowErrorPopup(false)}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showDuplicateNamePopup && (
            <div className="popup-overlay" onClick={() => setShowDuplicateNamePopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
                <div className="popup-header">
                  <div style={{ fontSize: 18, fontWeight: 700 }}>Duplicate Business Name</div>
                </div>
                <div className="popup-body">
                  <div style={{ marginBottom: 12 }}>
                    {duplicateNamePopupMessage || 'A vendor with this business name already exists. You must use a different name or link your profile with the existing busuiness record, if you are part of the organization.'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button type="button" className="apply-btn" onClick={() => setShowDuplicateNamePopup(false)}>OK</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Confirm specific vendor selection before linking */}
          {pendingVendorToLink && (
            <div className="popup-overlay" onClick={() => setPendingVendorToLink(null)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
                <div className="popup-header">
                  <div style={{ fontSize: 18, fontWeight: 700 }}>Confirm Link to {pendingVendorToLink.name}</div>
                </div>
                <div className="popup-body">
                  <div style={{ marginBottom: 12 }}>
                    You're about to request a link to <strong>{pendingVendorToLink.name}</strong> ({pendingVendorToLink.vendorNumber || pendingVendorToLink.id}). The vendor organizer will need to approve this request before you gain access to vendor-only features. Do you want to continue?
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      type="button"
                      className="apply-btn"
                      disabled={saving}
                      onClick={async () => {
                        const v = pendingVendorToLink;
                        if (!v || !targetUid) return;

                        try {
                          setSaving(true);
                          // Persist the link request immediately so vendor admins can see it
                          // without requiring an additional Profile Save click.
                          const persisted = await upsertUserProfile(targetUid, {
                            vendorId: v.id,
                            vendorRole: 'user',
                            linkApproval: 'pending',
                            updatedBy: currentUser?.uid || '',
                            updatedByName:
                              `${myProfile?.firstName || ''} ${myProfile?.lastName || ''}`.trim() ||
                              myProfile?.name ||
                              currentUser?.displayName ||
                              myProfile?.email ||
                              currentUser?.email ||
                              currentUser?.uid ||
                              '',
                          });

                          if (persisted && persisted.__error) {
                            setError(`Failed to submit link request: ${persisted.__error.message || persisted.__error.code}`);
                            setSaving(false);
                            return;
                          }

                          setProfile(prev => ({
                            ...prev,
                            vendorId: v.id,
                            vendorNumber: v.vendorNumber || '',
                            vendorStatus: v.vendorStatus || 'Active',
                            vendorAdmin: v.vendorAdmin || '',
                            businessName: v.name || '',
                            vendorRole: 'user',
                            linkApproval: 'pending',
                          }));
                          try { originalVendorId.current = v.id; } catch (_) {}

                          // Best-effort email notification to vendor contact.
                          try {
                            const requesterName = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
                              || profile?.displayName
                              || profile?.email
                              || 'A user';
                            const requesterEmail = (profile?.email || '').trim();
                            let toEmail = String(v?.email || '').trim();
                            try {
                              if (!toEmail) {
                                // Use the publicly-readable vendorDirectory instead of the
                                // protected vendors collection (non-members are denied that read).
                                const dirEntry = await getVendorDirectoryEntry(v.id);
                                toEmail = String(dirEntry?.email || '').trim();
                              }
                            } catch (_) {
                              // ignore: best-effort fallback above may already have a value
                            }

                            if (toEmail) {
                              const subject = `Vendor Access Request: ${v.name || v.id}`;
                              const body = `
                                <p>Dear Vendor Admin,</p>
                                <p>A user has requested access to your vendor account.</p>
                                <p><strong>Vendor:</strong> ${v.name || v.id}</p>
                                <p><strong>Requester Name:</strong> ${requesterName}</p>
                                <p><strong>Requester Email:</strong> ${requesterEmail || '-'}</p>
                                <p>Please sign in to ICNACON Portal and approve/reject this request from your home page. (Look at the bottom section)</p>
                              `;
                              await sendEmail({ to: toEmail, subject, type: 'HTML', body });
                            } else {
                              console.warn('Link request email skipped: vendor email is not available', { vendorId: v.id, vendorLabel: v.name || v.id });
                            }
                          } catch (notifyErr) {
                            console.warn('Failed to send link request email (non-blocking):', notifyErr?.message || notifyErr);
                          }

                          setVendorSetupMode('');
                          setDropdownOpen(false);
                          setVendorQuery(v.name || '');
                          setFilteredVendors([]);
                          if (!protectedVendorLoaded.current) setVendorLinkedFromDirectory(true);
                          setPendingVendorToLink(null);

                          if (successTimer.current) clearTimeout(successTimer.current);
                          setSuccessMessage(`Link request submitted for ${v.name || 'selected vendor'}.`);
                          successTimer.current = setTimeout(() => setSuccessMessage(''), 5000);
                        } catch (err) {
                          console.error('Failed submitting vendor link request', err);
                          setError(`Failed to submit link request: ${err?.message || err}`);
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      {saving ? 'Submitting…' : 'Yes, link to this vendor'}
                    </button>
                    <button type="button" className="cancel-btn" onClick={() => setPendingVendorToLink(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}
    </div>
  );
};

export default ProfilePage;
